import { formatArrayJoinValues, formatItemIdentity, formatDictionaryValue } from 'shared-lib/helpers/formatters';
import { format as formatTz, formatInTimeZone, getTimezoneOffset } from 'date-fns-tz';
import { parseISO } from 'date-fns';

const formatJoinArrayValuesOrReturnAll = value => {
    if (!value || !value.length) {
        return 'All';
    }

    return formatArrayJoinValues(value);
};

/**
 * Replace the null values in a given object with empty strings.
 * @param obj
 * @returns {{}}
 */
const replaceNullsWithEmptyStrings = obj => Object.keys(obj).reduce((accumulator, key) => {
    accumulator[key] = obj[key] === null ? '' : obj[key];

    return accumulator;
}, {});

const escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

const formatWithTimezoneToApi = (date, timeZone) => {
    const isoDate = parseISO(date);
    let isUtc = false;

    const timezoneOffset = getTimezoneOffset(timeZone, isoDate);
    if (timezoneOffset === 0) isUtc = true;

    const formattedWithTz = formatTz(isoDate, 'yyyy-MM-dd\'T\'HH:mm:ssXXX', { timeZone });
    return isUtc ? formattedWithTz.replace('Z', '+00:00') : formattedWithTz;
};

const formatInTimeZoneFromApi = (date, timeZone) => {
    return formatInTimeZone(parseISO(date), timeZone, 'yyyy-MM-dd HH:mm' );
};

const isoFormat = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}([+-]\d{2}:\d{2}|Z)$/;
export const isIsoFormat = (dateStr) => isoFormat.test(dateStr);

/**
 * Check if a value is valid JSON (string or object)
 * @param {string|object} value - Value to check
 * @returns {boolean}
 */
export const isJsonString = (value) => {
    // If it's already an object (not null, not array of primitives), it's JSON-like
    if (value && typeof value === 'object') {
        return true;
    }

    // If it's a string, try to parse it
    if (typeof value === 'string' && value.trim()) {
        try {
            JSON.parse(value);
            return true;
        } catch (e) {
            return false;
        }
    }

    return false;
};

/**
 * Format JSON value with proper indentation for readability
 * @param {string|object} value - Value to format (string or object)
 * @param {number} indent - Number of spaces for indentation (default: 2)
 * @returns {string} - Formatted JSON string or original value if not valid JSON
 */
export const formatJsonString = (value, indent = 2) => {
    if (!isJsonString(value)) {
        return value;
    }

    try {
        // If it's already an object, stringify it
        if (typeof value === 'object') {
            return JSON.stringify(value, null, indent);
        }

        // If it's a string, parse then stringify
        const parsed = JSON.parse(value);
        return JSON.stringify(parsed, null, indent);
    } catch (e) {
        return value;
    }
};

export {
    formatJoinArrayValuesOrReturnAll,
    replaceNullsWithEmptyStrings,
    escapeRegExp,
    formatItemIdentity,
    formatDictionaryValue,
    formatWithTimezoneToApi,
    formatInTimeZoneFromApi,
};
